import {Component, computed, effect, ElementRef, OnDestroy, OnInit, signal, viewChild} from '@angular/core';
import {Button} from 'primeng/button';
import {
  AuditEventType,
  AuditLevelType,
  AuditLogEventDetails,
  AuditSearchCriteria,
  BasicUserDetails,
  SystemAdminService
} from '../../../api';
import {LoadingService} from '../../../services/loading.service';
import {ErrorMessageService} from '../../../services/error-message.service';
import {TableModule} from 'primeng/table';
import {InputText} from 'primeng/inputtext';
import {MultiSelect} from 'primeng/multiselect';
import {DatePicker} from 'primeng/datepicker';
import {Card} from 'primeng/card';
import {FormsModule} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {TaAppStore} from '../../../state/clcta-store';
import {TaSearchDatasource} from '../../../datasources/ta-search-datasource';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {toEventMetaTypesText, toEventMetaValueText} from '../../../util/app.types';
import {filterTokens, HighlightSegment, highlightSegments, matchesTokens} from '../../../util/general.utils';

type AuditPreset = 'hour' | 'today' | 'week' | 'month' | 'custom';

interface AuditMetaCell {
  labelSegments: HighlightSegment[];
  valueSegments: HighlightSegment[];
  link?: string;
}

interface AuditRowView {
  event: AuditLogEventDetails;
  nameSegments: HighlightSegment[];
  emailSegments: HighlightSegment[];
  meta: AuditMetaCell[];
}

@Component({
  selector: 'clc-auditlog-admin',
  imports: [
    Button,
    TableModule,
    InputText,
    MultiSelect,
    DatePicker,
    Card,
    FormsModule,
    DatePipe,
    RouterLink,
    ScrollHeightDirective
  ],
  templateUrl: './clc-auditlog-admin.html',
  styleUrl: './clc-auditlog-admin.scss'
})
export class ClcAuditlogAdmin implements OnInit, OnDestroy {

  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;

  auditLogList = signal<AuditLogEventDetails[]>([]);
  totalCount = signal(0);
  hasSearched = signal(false);
  isLoading = signal(false);

  private currentPage = 0;
  private activeCriteria: AuditSearchCriteria | undefined;

  activePreset = signal<AuditPreset>('today');

  filters = {
    dateFrom: undefined as Date | undefined,
    dateTo: undefined as Date | undefined,
    users: [] as BasicUserDetails[],
    levels: [] as AuditLevelType[],
    eventTypes: [] as AuditEventType[],
  };

  filterInput = signal('');
  filterStr = signal('');
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  tokens = computed(() => filterTokens(this.filterStr()));
  hasFilter = computed(() => this.tokens().length > 0);
  canLoadMore = computed(() => this.auditLogList().length < this.totalCount());

  availableUsers = computed<BasicUserDetails[]>(() => this.store.userSearchOptions());
  private userNameById = computed(() =>
    new Map(this.availableUsers().map(user => [user.id, user.name])));

  levelOptions = Object.values(AuditLevelType).map(level => ({label: level, value: level}));
  eventTypeOptions = Object.values(AuditEventType).sort().map(type => ({label: type, value: type}));

  rows = computed<AuditRowView[]>(() => {
    const tokens = this.tokens();

    return this.auditLogList()
      .map(event => {
        const email = event.userEmail ?? '';
        const taLink = event.submissionId ? `/ta/${event.submissionId}/manage` : undefined;
        return {
          event,
          name: this.userNameById().get(event.userId ?? '') ?? (email === 'systemuser' ? 'System' : ''),
          email,
          // Comment metadata holds the stored comment text, so its mentions are flattened to
          // `@Name` before filtering and highlighting - the raw `@[Name](id)` token is not shown.
          // It carries no TA reference, so the event's submission id is shown ahead of it.
          meta: [
            ...(event.eventType === AuditEventType.CommentEvent && event.submissionId
              ? [{label: 'TA ID', value: event.submissionId, link: taLink}] : []),
            ...Object.entries(event.metadata ?? {})
              .map(([key, value]) => ({
                label: toEventMetaTypesText(key),
                value: toEventMetaValueText(key, value),
                link: key === 'taNumber' ? taLink : undefined,
              })),
          ],
        };
      })
      .filter(row => matchesTokens(
        [row.name, row.email, ...row.meta.map(meta => `${meta.label} ${meta.value}`)].join(' '), tokens))
      .map(row => ({
        event: row.event,
        nameSegments: highlightSegments(row.name, tokens),
        emailSegments: highlightSegments(row.email, tokens),
        meta: row.meta.map(meta => ({
          labelSegments: highlightSegments(meta.label, tokens),
          valueSegments: highlightSegments(meta.value, tokens),
          link: meta.link,
        })),
      }));
  });

  constructor(
    private systemApi: SystemAdminService,
    private host: ElementRef,
    private loadingService: LoadingService,
    private errorMessageService: ErrorMessageService,
    private store: TaAppStore,
    private taSearchDatasource: TaSearchDatasource,
  ) {
    effect(() => {
      const value = this.filterInput();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.filterStr.set(value), 200);
    });

    // The sentinel sits below a filtered list that can be far shorter than the loaded page, so
    // auto-loading is disabled while a filter is active - otherwise a filter matching nothing
    // keeps the sentinel in view and walks every remaining page. The button still works.
    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      const autoLoad = !this.hasFilter();
      this.observer?.disconnect();
      if (el && root && autoLoad) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          {root, threshold: 0.1},
        );
        this.observer.observe(el);
      }
    });
  }

  ngOnInit(): void {
    this.store.updateBreadCrumbData({
      title: 'Audit Log',
      titleIcon: 'fa-regular fa-rectangle-history fa-lg'
    });
    this.taSearchDatasource.loadUserOptionsForSearch();
    this.runSearch();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    clearTimeout(this.debounceTimer);
  }

  applyPreset(preset: AuditPreset): void {
    this.activePreset.set(preset);
    this.runSearch();
  }

  onCustomRangeChange(): void {
    this.activePreset.set('custom');
  }

  runSearch(): void {
    this.activeCriteria = this.buildCriteria();
    this.currentPage = 0;
    this.auditLogList.set([]);
    this.totalCount.set(0);
    this.fetch(0, false);
  }

  loadMore(): void {
    if (!this.activeCriteria || !this.canLoadMore() || this.isLoading()) {
      return;
    }
    this.currentPage++;
    this.fetch(this.currentPage, true);
  }

  clearSearch(): void {
    this.activePreset.set('today');
    this.filters = {
      dateFrom: undefined,
      dateTo: undefined,
      users: [],
      levels: [],
      eventTypes: [],
    };
    this.clearFilter();
    this.runSearch();
  }

  clearFilter(): void {
    clearTimeout(this.debounceTimer);
    this.filterInput.set('');
    this.filterStr.set('');
  }

  /**
   * Resolves a preset to an absolute window. Uses the date constructor rather than millisecond
   * arithmetic so negative day numbers normalise across month boundaries and daylight saving.
   */
  private resolvePresetRange(preset: AuditPreset): {from: Date; to: Date} | undefined {
    const now = new Date();
    const startOfDayBefore = (days: number) =>
      new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);

    switch (preset) {
      case 'hour':
        return {from: new Date(now.getTime() - 60 * 60 * 1000), to: now};
      case 'today':
        return {from: startOfDayBefore(0), to: now};
      case 'week':
        return {from: startOfDayBefore(6), to: now};
      case 'month':
        return {from: startOfDayBefore(29), to: now};
      default:
        return undefined;
    }
  }

  private endOfDay(date?: Date): Date | undefined {
    return date
      ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
      : undefined;
  }

  private buildCriteria(): AuditSearchCriteria {
    const range = this.resolvePresetRange(this.activePreset());

    // The date pickers land on midnight, so the upper bound is pushed to the end of the chosen
    // day - otherwise a range ending today excludes everything that happened today.
    const from = range?.from ?? this.filters.dateFrom;
    const to = range?.to ?? this.endOfDay(this.filters.dateTo);

    return {
      dateFrom: from?.toISOString(),
      dateTo: to?.toISOString(),
      userIds: this.filters.users.map(user => user.id!).filter(id => !!id),
      levels: this.filters.levels,
      eventTypes: this.filters.eventTypes,
      page: 0,
    };
  }

  /**
   * Pages against the criteria snapshot taken when the search ran. Rebuilding them here would
   * re-resolve a relative preset against the current clock and slide the window between pages.
   */
  private fetch(page: number, append: boolean): void {
    this.isLoading.set(true);
    this.loadingService.showLoading({message: 'Loading Audit Logs', targetElement: this.host});

    this.systemApi.searchAuditLog({...this.activeCriteria!, page}).subscribe({
      next: resp => {
        if (resp.success && resp.data) {
          const results = resp.data.results ?? [];
          this.auditLogList.update(list => append ? [...list, ...results] : results);
          this.totalCount.set(resp.data.totalCount ?? 0);
        } else {
          if (append) this.currentPage--;
          this.errorMessageService.showTemporaryError('Failed to load audit events: ' + resp.error, 5000);
        }
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.loadingService.hideLoading();
      },
      error: error => {
        if (append) this.currentPage--;
        console.error('error loading audit logs: ', error);
        this.errorMessageService.showTemporaryError('Failed to load audit events.', 5000);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.loadingService.hideLoading();
      }
    });
  }

}
